// src/components/ModalManutencaoWeb.jsx
// Fixes:
//   [URL-FIX]   resolveUrl() — URLs relativas são prefixadas com o host da API
//               (front em blbsystem.com.br, API em crm-blb-system.onrender.com)
//   [DEDUP-FIX] handleEnviar salva o ID real do servidor e substitui o local_xxx
//               ANTES do socket disparar → dedup funciona para texto e anexos
//   [DEDUP-FIX] socket handler e pollChat usam correspondência por arquivo.url
//               além de texto, para não duplicar mensagens só-arquivo
//   [AUDIO-FIX] AudioPlayer recebe URL já resolvida via resolveUrl()

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '../services/api';
import './ModalManutencaoWeb.css';
import {
  FaTimes, FaRoute, FaCheck, FaSync,
  FaExclamationTriangle, FaComments, FaRedo,
  FaPaperclip, FaDownload, FaMicrophone, FaStop, FaPlay,
} from 'react-icons/fa';
import { MdCarRepair } from 'react-icons/md';

// ── [URL-FIX] Resolve URLs relativas contra o host da API ────────────────────
// O front pode estar em domínio diferente da API (ex: CDN / Render).
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
const resolveUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) return url;
  return `${API_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
};

const MAPBOX_TOKEN = 'pk.eyJ1IjoiYmxuYXNjaW1lbnRvMjQwMjk4IiwiYSI6ImNtZ2lnYnc1NTA5MmEycG9pM2M5M3IwcWMifQ.JcuK1f9M-yZdwCeTXDx20Q';

const calcDist = (la1, lo1, la2, lo2) => {
  if (!la1 || !lo1 || !la2 || !lo2) return null;
  const R = 6371, r = v => v * Math.PI / 180;
  const a = Math.sin(r(la2-la1)/2)**2 + Math.cos(r(la1))*Math.cos(r(la2))*Math.sin(r(lo2-lo1)/2)**2;
  return +(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))).toFixed(1);
};
const formatMin  = dist => dist ? Math.round(dist / 0.45) : null;
const formatSecs = s => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

const TIPOS = {
  motor:       { label: 'Problema no motor',   icon: '🔧', color: '#ef4444' },
  pneu:        { label: 'Pneu furado',          icon: '⚫', color: '#f97316' },
  bateria:     { label: 'Bateria descarregada', icon: '🔋', color: '#eab308' },
  acidente:    { label: 'Acidente',             icon: '🚨', color: '#dc2626' },
  combustivel: { label: 'Sem combustível',      icon: '⛽', color: '#f59e0b' },
  outro:       { label: 'Outro problema',       icon: '⚠️', color: '#94a3b8' },
};
const PARCEIRO_ICONS = { oficina:'🔧', patio:'🏭', posto:'⛽', policia:'🛡️', hospital:'🚑', restaurante:'🍽️' };

// ── AudioPlayer — recebe URL já resolvida ─────────────────────────────────────
function AudioPlayer({ url, nome }) {
  const audioRef  = useRef(null);
  const [playing,  setPlaying]  = useState(false);
  const [current,  setCurrent]  = useState(0);
  const [duration, setDuration] = useState(0);
  const [error,    setError]    = useState(false);

  const toggle = () => {
    if (!audioRef.current || error) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else         { audioRef.current.play().catch(() => setError(true)); setPlaying(true); }
  };

  return (
    <div className="mw-audio-player">
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        crossOrigin="anonymous"
        onLoadedMetadata={e => setDuration(e.target.duration)}
        onTimeUpdate={e  => setCurrent(e.target.currentTime)}
        onEnded={() => { setPlaying(false); setCurrent(0); }}
        onError={() => setError(true)}
      />
      <button className={`mw-audio-play-btn ${error ? 'errored' : ''}`} onClick={toggle} disabled={error} title={error ? 'Erro ao carregar áudio' : ''}>
        {playing ? <FaStop/> : <FaPlay/>}
      </button>
      <div className="mw-audio-info">
        <div className="mw-audio-bar">
          <div className="mw-audio-progress" style={{ width: duration ? `${(current/duration)*100}%` : '0%' }} />
        </div>
        <span className="mw-audio-time">
          {error ? 'Erro ao carregar' : `${formatSecs(Math.floor(current))} / ${formatSecs(Math.floor(duration || 0))}`}
        </span>
      </div>
      <a href={url} download={nome} className="mw-audio-dl" title="Baixar áudio">
        <FaDownload style={{ fontSize: 11 }}/>
      </a>
    </div>
  );
}

export default function ModalManutencaoWeb({
  sessao,
  parceiros    = [],
  fornecedores = [],
  socket,
  onClose,
}) {
  const [msgs,          setMsgs]          = useState([]);
  const [input,         setInput]         = useState('');
  const [parceiroSel,   setParceiroSel]   = useState(null);
  const [postoSel,      setPostoSel]      = useState(null);
  const [rotaEnviada,   setRotaEnviada]   = useState(false);
  const [carregando,    setCarregando]    = useState(true);
  const [enviandoMsg,   setEnviandoMsg]   = useState(false);
  const [enviandoRota,  setEnviandoRota]  = useState(false);
  const [resolvendo,    setResolvendo]    = useState(false);
  const [tick,          setTick]          = useState(0);
  const [tab,           setTab]           = useState('parceiros');

  const [postos,        setPostos]        = useState([]);
  const [loadingPostos, setLoadingPostos] = useState(false);
  const [postosError,   setPostosError]   = useState(null);
  const [postosFetched, setPostosFetched] = useState(false);
  const [apiParceiros,  setApiParceiros]  = useState([]);
  const [geoCache,      setGeoCache]      = useState({});
  const geocodeAttempted = useRef(new Set());

  // Anexo (arquivo)
  const fileInputRef   = useRef(null);
  const [uploadingArq, setUploadingArq] = useState(false);
  const [previewArq,   setPreviewArq]   = useState(null);

  // Gravação de áudio
  const [gravando,      setGravando]      = useState(false);
  const [tempoGrav,     setTempoGrav]     = useState(0);
  const [previewAudio,  setPreviewAudio]  = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef   = useRef([]);
  const gravTimerRef     = useRef(null);

  const chatRef  = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t+1), 1000);
    return () => clearInterval(id);
  }, []);

  // ── Normaliza mensagem do servidor ────────────────────────────────────────
  const normalizeMsg = (m) => ({
    id:      m.id,
    de:      m.de,
    txt:     m.texto ?? m.txt ?? '',
    op:      m.operador_nome ?? m.op ?? null,
    t:       m.enviada_em
      ? new Date(m.enviada_em).toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit' })
      : (m.t || ''),
    arquivo: m.arquivo
      ? (typeof m.arquivo === 'string' ? JSON.parse(m.arquivo) : m.arquivo)
      : null,
  });

  // ── Carga inicial ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!sessao?.sessionId) return;
    setCarregando(true);
    api.get(`/api/operations/manutencao/${sessao.sessionId}/chat`)
      .then(({ data }) => setMsgs(data.map(normalizeMsg)))
      .catch(err => console.error('[chat load]', err.message))
      .finally(() => setCarregando(false));
  }, [sessao?.sessionId]);

  // ── [DEDUP-FIX] pollChat — dedup por texto E por arquivo.url ─────────────
  const pollChat = useCallback(async () => {
    if (!sessao?.sessionId) return;
    try {
      const { data } = await api.get(`/api/operations/manutencao/${sessao.sessionId}/chat`);
      const server = data.map(normalizeMsg);
      setMsgs(prev => {
        const pending = prev.filter(m => {
          if (!String(m.id).startsWith('local_')) return false; // server msgs will be replaced
          // Mantém local_ se ainda não chegou no servidor
          const inServer = server.some(sm =>
            (sm.de === m.de) && (
              (sm.txt && m.txt && sm.txt === m.txt) ||
              (sm.arquivo?.url && m.arquivo?.url && sm.arquivo.url === m.arquivo.url)
            )
          );
          return !inServer;
        });
        return [...server, ...pending];
      });
    } catch {}
  }, [sessao?.sessionId]);

  useEffect(() => {
    if (carregando) return;
    const id = setInterval(pollChat, 3500);
    return () => clearInterval(id);
  }, [carregando, pollChat]);

  // ── Socket ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!socket) return;

    // [DEDUP-FIX] dedup por ID real, texto E arquivo.url
    const onMsg = payload => {
      if (Number(payload.sessionId) !== Number(sessao?.sessionId)) return;
      setMsgs(prev => {
        // Já tem o ID real (local foi substituído pela resposta do POST)
        if (prev.some(m => m.id === payload.id)) return prev;

        // Remove local_ correspondente (texto ou arquivo)
        const clean = prev.filter(m => {
          if (!String(m.id).startsWith('local_')) return true;
          if (m.de !== payload.de) return true;
          if (payload.txt && m.txt && m.txt === payload.txt) return false;
          if (!payload.txt && !m.txt && payload.arquivo?.url && m.arquivo?.url === payload.arquivo.url) return false;
          return true;
        });

        return [...clean, {
          id:      payload.id || Date.now(),
          de:      payload.de,
          txt:     payload.txt || '',
          op:      payload.operadorNome || payload.op || null,
          t:       payload.t,
          arquivo: payload.arquivo || null,
        }];
      });
    };

    const onRota = p => { if (Number(p.sessionId) === Number(sessao?.sessionId)) setRotaEnviada(true); };
    const onDes  = p => { if (Number(p.sessionId) === Number(sessao?.sessionId)) onClose(); };

    socket.on('manutencao:chat_msg',     onMsg);
    socket.on('manutencao:rota_enviada', onRota);
    socket.on('manutencao:desativada',   onDes);
    return () => {
      socket.off('manutencao:chat_msg',     onMsg);
      socket.off('manutencao:rota_enviada', onRota);
      socket.off('manutencao:desativada',   onDes);
    };
  }, [socket, sessao?.sessionId, onClose]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [msgs]);

  useEffect(() => {
    api.get('/api/operations/parceiros-logisticos')
      .then(({ data }) => setApiParceiros(Array.isArray(data.data) ? data.data : []))
      .catch(() => {});
  }, []);

  const todosParceiros = useMemo(() => {
    const mapa = new Map();
    const add = (item, prefix = '') => {
      const key  = `${prefix}${item.id}`;
      const nome = item.nome_fantasia || item.nome || '—';
      const tipo = item.tipo || 'oficina';
      let end = item.endereco;
      if (!end) {
        const pts = [item.logradouro, item.numero, item.bairro, item.cidade, item.estado].filter(Boolean);
        if (pts.length) end = pts.join(', ');
      }
      const lat = geoCache[key]?.latitude  || item.latitude;
      const lon = geoCache[key]?.longitude || item.longitude;
      if (!mapa.has(key)) mapa.set(key, { ...item, _key: key, nome_fantasia: nome, tipo, endereco: end, latitude: lat, longitude: lon });
    };
    parceiros.forEach(p    => add(p));
    apiParceiros.forEach(p => add(p));
    fornecedores.forEach(p => add(p, 'ext_'));
    return Array.from(mapa.values())
      .map(p => { const dist = calcDist(sessao?.latitude, sessao?.longitude, p.latitude, p.longitude); return { ...p, dist, min: dist ? formatMin(dist) : null }; })
      .sort((a,b) => { if(a.dist===null&&b.dist===null) return 0; if(a.dist===null) return 1; if(b.dist===null) return -1; return a.dist-b.dist; });
  }, [parceiros, apiParceiros, fornecedores, sessao?.latitude, sessao?.longitude, geoCache]);

  useEffect(() => {
    todosParceiros.filter(p => !p.latitude && !p.longitude && p.endereco && !geocodeAttempted.current.has(p._key))
      .forEach(async p => {
        geocodeAttempted.current.add(p._key);
        try {
          const res  = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(p.endereco)}.json?access_token=${MAPBOX_TOKEN}&country=BR&language=pt&limit=1`);
          const data = await res.json();
          if (data.features?.[0]) {
            const [lon, lat] = data.features[0].center;
            setGeoCache(prev => ({ ...prev, [p._key]: { latitude: lat, longitude: lon } }));
          }
        } catch {}
      });
  }, [todosParceiros]);

  const fetchPostos = useCallback(async () => {
    if (!sessao?.latitude || !sessao?.longitude) { setPostosError('Localização indisponível.'); return; }
    setLoadingPostos(true); setPostosError(null);
    try {
      const q   = `[out:json][timeout:15];node["amenity"="fuel"](around:8000,${sessao.latitude},${sessao.longitude});out 20;`;
      const res = await fetch(`https://overpass-api.de/api/interpreter?data=${encodeURIComponent(q)}`);
      if (!res.ok) throw new Error('Overpass ' + res.status);
      const data = await res.json();
      const lista = (data?.elements||[]).filter(e=>e.lat&&e.lon).map(e => ({
        id: e.id, nome: e.tags?.name||e.tags?.brand||'Posto de Combustível', marca: e.tags?.brand||null,
        endereco: [e.tags?.['addr:street'],e.tags?.['addr:housenumber']].filter(Boolean).join(', '),
        telefone: e.tags?.phone||null, latitude: e.lat, longitude: e.lon,
        dist: calcDist(sessao.latitude, sessao.longitude, e.lat, e.lon),
      })).filter(p=>p.dist!==null).sort((a,b)=>a.dist-b.dist);
      setPostos(lista.map(p=>({...p,min:formatMin(p.dist)})));
      setPostosFetched(true);
    } catch { setPostosError('Não foi possível buscar postos próximos.'); }
    finally { setLoadingPostos(false); }
  }, [sessao?.latitude, sessao?.longitude]);

  useEffect(() => {
    if (tab==='postos' && !postosFetched && !loadingPostos) fetchPostos();
  }, [tab, postosFetched, loadingPostos, fetchPostos]);

  // ── Arquivo ────────────────────────────────────────────────────────────────
  const handleFileChange = e => {
    const file = e.target.files?.[0]; if (!file) return;
    const isImg = file.type.startsWith('image/');
    setPreviewArq({ file, previewUrl: isImg ? URL.createObjectURL(file) : null, nome: file.name, tipo: file.type });
    e.target.value = null;
  };
  const clearPreview = () => {
    if (previewArq?.previewUrl) URL.revokeObjectURL(previewArq.previewUrl);
    setPreviewArq(null);
  };

  // ── Gravação de áudio ──────────────────────────────────────────────────────
  const iniciarGravacao = async () => {
    if (!navigator.mediaDevices?.getUserMedia) { alert('Seu navegador não suporta gravação de áudio.'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr     = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      audioChunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const url  = URL.createObjectURL(blob);
        const nome = `audio_${Date.now()}.webm`;
        setPreviewAudio({ blob, url, nome, tipo: 'audio/webm' });
        clearInterval(gravTimerRef.current);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setGravando(true);
      setTempoGrav(0);
      setPreviewArq(null);
      setPreviewAudio(null);
      gravTimerRef.current = setInterval(() => setTempoGrav(t => t + 1), 1000);
    } catch { alert('Não foi possível acessar o microfone. Verifique as permissões do navegador.'); }
  };

  const pararGravacao = () => { mediaRecorderRef.current?.stop(); setGravando(false); };

  const cancelarGravacao = () => {
    if (mediaRecorderRef.current?.state !== 'inactive') {
      audioChunksRef.current = [];
      mediaRecorderRef.current?.stop();
    }
    setGravando(false); setTempoGrav(0); clearInterval(gravTimerRef.current);
  };

  const clearAudio = () => {
    if (previewAudio?.url) URL.revokeObjectURL(previewAudio.url);
    setPreviewAudio(null);
  };

  // ── [DEDUP-FIX] Enviar mensagem ────────────────────────────────────────────
  // 1. Gera clientId único
  // 2. Adiciona mensagem otimista com id = clientId e txt = texto real
  // 3. Após POST, substitui clientId pelo ID real do servidor
  //    → quando o socket disparar, encontra o ID real e não duplica
  const handleEnviar = async () => {
    const texto = input.trim();
    if (!texto && !previewArq && !previewAudio) return;
    if (enviandoMsg || uploadingArq || gravando) return;

    setInput('');
    setEnviandoMsg(true);
    let arquivoPayload = null;

    // Upload do anexo primeiro
    const anexo = previewAudio || previewArq;
    if (anexo) {
      setUploadingArq(true);
      try {
        const fd = new FormData();
        if (previewAudio) { fd.append('arquivo', previewAudio.blob, previewAudio.nome); clearAudio(); }
        else              { fd.append('arquivo', previewArq.file);                       clearPreview(); }
        const { data: up } = await api.post(
          `/api/operations/manutencao/${sessao.sessionId}/chat/upload`,
          fd, { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        arquivoPayload = { url: up.url, nome: up.nome || anexo.nome, tipo: up.tipo || anexo.tipo };
      } catch (err) { console.error('[upload]', err.message); }
      finally { setUploadingArq(false); }
    }

    // ID temporário para tracking do otimista
    const clientId = `local_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

    const msgLocal = {
      id:      clientId,
      de:      'central',
      txt:     texto,         // ← texto real (vazio se só arquivo); NÃO mais o nome do arquivo
      op:      'Você — Central',
      t:       new Date().toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit' }),
      arquivo: arquivoPayload,
    };
    setMsgs(prev => [...prev, msgLocal]);

    try {
      const { data: saved } = await api.post(`/api/operations/manutencao/${sessao.sessionId}/chat`, {
        texto,
        arquivo_url:  arquivoPayload?.url  || null,
        arquivo_nome: arquivoPayload?.nome || null,
        arquivo_tipo: arquivoPayload?.tipo || null,
      });

      // [DEDUP-FIX] substitui clientId pelo ID real do servidor
      // Quando o socket disparar para este cliente, encontrará o ID real → não duplica
      if (saved?.id) {
        setMsgs(prev => prev.map(m => m.id === clientId ? { ...m, id: saved.id } : m));
      }
    } catch (err) { console.error('[send]', err.message); }
    finally { setEnviandoMsg(false); inputRef.current?.focus(); }
  };

  const handleEnviarRota = async (lista, selId) => {
    if (!selId||rotaEnviada||enviandoRota) return;
    const p = lista.find(x => x._key===selId || String(x.id)===String(selId));
    if (!p) return;
    setEnviandoRota(true);
    try {
      await api.post(`/api/operations/manutencao/${sessao.sessionId}/enviar-rota`, {
        parceiroId: String(p.id), parceiroNome: p.nome_fantasia||p.nome,
        dist: p.dist, min: p.min, tel: p.telefone||'', endereco: p.endereco||'',
      });
      setRotaEnviada(true);
    } catch (err) { console.error('[rota]', err.message); }
    finally { setEnviandoRota(false); }
  };

  const handleResolver = async () => {
    if (!window.confirm('Confirmar que a emergência foi resolvida?')) return;
    setResolvendo(true);
    try { await api.put(`/api/operations/manutencao/${sessao.sessionId}/desativar`); onClose(); }
    catch (err) { console.error('[desativar]', err.message); setResolvendo(false); }
  };

  const elapsed    = sessao?.ativada_em ? Math.floor((Date.now() - new Date(sessao.ativada_em).getTime())/1000) : 0;
  const elapsedFmt = formatSecs(elapsed);
  const prob       = TIPOS[sessao?.problema_tipo] || TIPOS.outro;
  const semGPS     = !sessao?.latitude || !sessao?.longitude;
  const selParceiro = parceiroSel ? todosParceiros.find(x=>x._key===parceiroSel||String(x.id)===String(parceiroSel)) : null;
  const selPosto    = postoSel    ? postos.find(x=>String(x.id)===String(postoSel)) : null;
  const selAtual    = selParceiro || selPosto;
  const rotaLista   = parceiroSel ? todosParceiros : postos;
  const rotaSelId   = parceiroSel || postoSel;

  // ── [URL-FIX] Render de mensagem ──────────────────────────────────────────
  const renderMsg = (msg) => {
    if (msg.de === 'sistema') {
      return (
        <div key={msg.id} className="mw-msg-sistema">
          <span>📍 {msg.txt}</span>
          <span className="mw-msg-time">{msg.t}</span>
        </div>
      );
    }
    const isMotorista = msg.de === 'motorista';
    const arq         = msg.arquivo;
    const temArq      = !!arq?.url;
    const ehImagem    = arq?.tipo?.startsWith('image/');
    const ehAudio     = arq?.tipo?.startsWith('audio/');
    const arqUrl      = temArq ? resolveUrl(arq.url) : null; // [URL-FIX]

    return (
      <div key={msg.id} className={`mw-msg-row ${isMotorista ? 'esq' : 'dir'}`}>
        <span className="mw-msg-op">
          {isMotorista ? sessao?.motoristaNome?.split(' ')[0] : (msg.op || 'Central')}
        </span>
        <div className={`mw-bubble ${isMotorista ? 'bubble-motorista' : 'bubble-central'}`}>
          {msg.txt && <span>{msg.txt}</span>}

          {/* [URL-FIX] Imagem */}
          {temArq && ehImagem && (
            <div className="mw-msg-img-wrap">
              <img
                src={arqUrl}
                alt={arq.nome}
                className="mw-msg-img"
                onClick={() => window.open(arqUrl, '_blank')}
                onError={e => { e.target.style.display='none'; }}
              />
              <span className="mw-msg-arq-nome">{arq.nome}</span>
            </div>
          )}

          {/* [URL-FIX] Áudio — player inline com URL resolvida */}
          {temArq && ehAudio && (
            <AudioPlayer url={arqUrl} nome={arq.nome} />
          )}

          {/* [URL-FIX] Documento */}
          {temArq && !ehImagem && !ehAudio && (
            <a href={arqUrl} target="_blank" rel="noopener noreferrer" className="mw-msg-arq-link">
              <FaDownload style={{ flexShrink:0 }} />
              <span>{arq.nome}</span>
            </a>
          )}
        </div>
        <span className="mw-msg-time">{msg.t}</span>
      </div>
    );
  };

  return (
    <div className="mw-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="mw-modal">

        {/* HEADER */}
        <header className="mw-header">
          <div className="mw-header-left">
            <div className="mw-emergency-icon"><FaExclamationTriangle/></div>
            <div>
              <div className="mw-header-title">EMERGÊNCIA DE FROTA</div>
              <div className="mw-header-sub">
                <span>{sessao?.motoristaNome}</span>
                <span className="mw-dot">·</span>
                <code>{sessao?.placa||'—'}</code>
                <span className="mw-dot">·</span>
                <span className="mw-timer">{elapsedFmt}</span>
                <span className="mw-dot">·</span>
                <span className="mw-polling-badge">🔄 auto-sync 3,5s</span>
              </div>
            </div>
          </div>
          <div className="mw-header-right">
            <span className="mw-live-badge">● AO VIVO</span>
            <button className="mw-btn-resolver" onClick={handleResolver} disabled={resolvendo}>
              {resolvendo ? <FaSync className="mw-spin"/> : <FaCheck/>}
              <span>{resolvendo ? 'Encerrando...' : 'Resolver'}</span>
            </button>
            <button className="mw-btn-close" onClick={onClose}><FaTimes/></button>
          </div>
        </header>

        <div className="mw-body">
          {/* PAINEL ESQUERDO */}
          <div className="mw-left">
            <div className="mw-driver-card">
              <div className="mw-driver-avatar"><MdCarRepair size={20} color="#ef4444"/></div>
              <div className="mw-driver-info">
                <span className="mw-driver-name">{sessao?.motoristaNome}</span>
                <span className="mw-driver-vehicle">
                  {[sessao?.marca,sessao?.modelo].filter(Boolean).join(' ')||'—'} · {sessao?.placa||'—'}
                </span>
              </div>
              <span className="mw-status-badge">PARALISADO</span>
            </div>

            <div className="mw-problem-card" style={{ borderLeftColor: prob.color }}>
              <div className="mw-problem-header">
                <span>{prob.icon}</span>
                <span className="mw-problem-type" style={{ color: prob.color }}>{prob.label.toUpperCase()}</span>
              </div>
              {sessao?.problema_desc && <p className="mw-problem-desc">{sessao.problema_desc}</p>}
            </div>

            <div className="mw-tabs">
              <button className={`mw-tab ${tab==='parceiros'?'active':''}`} onClick={()=>setTab('parceiros')}>
                🗺 Parceiros <span className="mw-tab-count">{todosParceiros.length}</span>
              </button>
              <button className={`mw-tab ${tab==='postos'?'active':''}`} onClick={()=>setTab('postos')}>
                ⛽ Postos {postosFetched&&<span className="mw-tab-count">{postos.length}</span>}
              </button>
              <button className={`mw-tab ${tab==='info'?'active':''}`} onClick={()=>setTab('info')}>📋 Info</button>
            </div>

            <div className="mw-tab-content">
              {tab==='parceiros'&&(
                todosParceiros.length===0
                  ? <div className="mw-sem-gps"><FaSync className="mw-spin" style={{fontSize:14}}/><span>Carregando...</span></div>
                  : <div className="mw-parceiros-list">
                      {semGPS&&<div className="mw-aviso"><FaExclamationTriangle/><span>GPS indisponível — distâncias não calculadas</span></div>}
                      {todosParceiros.map(p=>{
                        const sel = parceiroSel===p._key||parceiroSel===String(p.id);
                        return (
                          <div key={p._key||p.id}
                            className={`mw-parceiro-item ${sel?'selected':''} ${!p.dist?'no-gps':''}`}
                            onClick={()=>{ if(!rotaEnviada){setParceiroSel(p._key||String(p.id));setPostoSel(null);} }}>
                            <div className="mw-parceiro-left">
                              <span className="mw-parceiro-icon">{PARCEIRO_ICONS[p.tipo]||'📍'}</span>
                              <div className="mw-parceiro-info">
                                <span className="mw-parceiro-nome">{p.nome_fantasia}</span>
                                {p.endereco&&<span className="mw-parceiro-end">{p.endereco}</span>}
                                {sel&&p.telefone&&<span className="mw-parceiro-tel">📞 {p.telefone}</span>}
                              </div>
                            </div>
                            {p.dist!==null
                              ? <div className="mw-parceiro-dist"><span className="mw-dist-km">{p.dist} km</span><span className="mw-dist-min">~{p.min} min</span></div>
                              : <span className="mw-dist-nogps"><FaSync className="mw-spin" style={{marginRight:4,fontSize:10}}/>Buscando...</span>}
                          </div>
                        );
                      })}
                    </div>
              )}

              {tab==='postos'&&(
                loadingPostos
                  ? <div className="mw-postos-loading"><FaSync className="mw-spin"/><span>Buscando postos próximos…</span></div>
                  : postosError
                    ? <div className="mw-postos-error"><FaExclamationTriangle/><span>{postosError}</span>
                        <button className="mw-btn-retry" onClick={()=>{setPostosFetched(false);fetchPostos();}}><FaRedo/> Tentar novamente</button>
                      </div>
                    : <div className="mw-parceiros-list">
                        {postos.map(p=>{
                          const sel=String(postoSel)===String(p.id);
                          return (
                            <div key={p.id} className={`mw-parceiro-item mw-posto-item ${sel?'selected':''}`}
                              onClick={()=>{if(!rotaEnviada){setPostoSel(String(p.id));setParceiroSel(null);}}}>
                              <div className="mw-parceiro-left">
                                <span className="mw-parceiro-icon">⛽</span>
                                <div className="mw-parceiro-info">
                                  <span className="mw-parceiro-nome">{p.nome}</span>
                                  {p.endereco&&<span className="mw-parceiro-end">{p.endereco}</span>}
                                </div>
                              </div>
                              <div className="mw-parceiro-dist"><span className="mw-dist-km">{p.dist} km</span></div>
                            </div>
                          );
                        })}
                      </div>
              )}

              {tab==='info'&&(
                <div className="mw-info-list">
                  <div className="mw-info-row"><span className="mw-info-key">⏱ Ativado há</span><span className="mw-info-val">{elapsedFmt}</span></div>
                  <div className="mw-info-row"><span className="mw-info-key">⚠️ Tipo</span><span className="mw-info-val">{prob.label}</span></div>
                  <div className="mw-info-row"><span className="mw-info-key">📍 Localização</span><span className="mw-info-val">{sessao?.latitude ? `${Number(sessao.latitude).toFixed(5)}, ${Number(sessao.longitude).toFixed(5)}` : '—'}</span></div>
                  <div className="mw-info-row"><span className="mw-info-key">💬 Mensagens</span><span className="mw-info-val">{msgs.filter(m=>m.de!=='sistema').length}</span></div>
                </div>
              )}
            </div>

            <div className="mw-route-action">
              {rotaEnviada ? (
                <div className="mw-rota-ok">
                  <FaCheck/>
                  <div>
                    <div className="mw-rota-ok-title">Rota enviada com sucesso!</div>
                    {selAtual&&<div className="mw-rota-ok-sub">{selAtual.nome_fantasia||selAtual.nome} · {selAtual.dist} km</div>}
                  </div>
                </div>
              ) : (
                <button className={`mw-btn-rota ${!rotaSelId?'disabled':''}`}
                  onClick={()=>handleEnviarRota(rotaLista,rotaSelId)} disabled={!rotaSelId||enviandoRota}>
                  {enviandoRota?<FaSync className="mw-spin"/>:<FaRoute/>}
                  <span>{enviandoRota?'Enviando...' : selAtual ? `Enviar rota — ${selAtual.nome_fantasia||selAtual.nome} (${selAtual.dist} km)` : 'Selecione um parceiro ou posto'}</span>
                </button>
              )}
            </div>
          </div>

          {/* CHAT */}
          <div className="mw-right">
            <div className="mw-chat-header">
              <FaComments/>
              <span>Chat em tempo real</span>
              <div className="mw-connected-dot"/>
              <span className="mw-connected-label">CONECTADO</span>
            </div>

            <div ref={chatRef} className="mw-chat-messages">
              {carregando
                ? <div className="mw-chat-loading"><FaSync className="mw-spin"/> Carregando conversa...</div>
                : msgs.length===0
                  ? <div className="mw-chat-empty"><FaComments/><span>Aguardando mensagens.</span></div>
                  : msgs.map(renderMsg)}
            </div>

            {/* Barra de gravação ativa */}
            {gravando && (
              <div className="mw-gravando-bar">
                <div className="mw-gravando-pulse"/>
                <span className="mw-gravando-tempo">● Gravando… {formatSecs(tempoGrav)}</span>
                <button className="mw-btn-grav-cancel" onClick={cancelarGravacao} title="Cancelar"><FaTimes/></button>
                <button className="mw-btn-grav-stop" onClick={pararGravacao} title="Parar e enviar"><FaStop/> Parar</button>
              </div>
            )}

            {/* Preview arquivo */}
            {previewArq && !gravando && (
              <div className="mw-arq-preview">
                {previewArq.tipo?.startsWith('image/')
                  ? <img src={previewArq.previewUrl} alt="preview" className="mw-arq-preview-img"/>
                  : <div className="mw-arq-preview-doc"><FaPaperclip/></div>}
                <div className="mw-arq-preview-info">
                  <span className="mw-arq-preview-nome">{previewArq.nome}</span>
                  <span className="mw-arq-preview-sub">Pronto para enviar</span>
                </div>
                <button className="mw-arq-preview-cancel" onClick={clearPreview}><FaTimes/></button>
              </div>
            )}

            {/* Preview áudio gravado — usa URL de blob (local), não precisa de resolveUrl */}
            {previewAudio && !gravando && (
              <div className="mw-arq-preview">
                <div className="mw-arq-preview-doc mw-preview-audio-icon"><FaMicrophone/></div>
                <div className="mw-arq-preview-info">
                  <AudioPlayer url={previewAudio.url} nome={previewAudio.nome}/>
                  <span className="mw-arq-preview-sub">Pronto para enviar</span>
                </div>
                <button className="mw-arq-preview-cancel" onClick={clearAudio}><FaTimes/></button>
              </div>
            )}

            {/* Input */}
            <div className="mw-chat-input-area">
              <input ref={fileInputRef} type="file" style={{ display:'none' }}
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                onChange={handleFileChange}/>
              <button className="mw-btn-attach" onClick={()=>fileInputRef.current?.click()}
                title="Anexar arquivo" disabled={uploadingArq||gravando}>
                {uploadingArq?<FaSync className="mw-spin"/>:<FaPaperclip/>}
              </button>
              <button
                className={`mw-btn-attach mw-btn-mic ${gravando ? 'mw-btn-mic-active' : ''}`}
                onClick={gravando ? pararGravacao : iniciarGravacao}
                title={gravando ? 'Parar gravação' : 'Gravar áudio'}
                disabled={uploadingArq}
              >
                {gravando ? <FaStop/> : <FaMicrophone/>}
              </button>
              <input ref={inputRef} type="text" className="mw-chat-input"
                placeholder="Mensagem… (Enter para enviar)"
                value={input} onChange={e=>setInput(e.target.value)}
                disabled={gravando}
                onKeyDown={e=>{ if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();handleEnviar();} }}/>
              <button
                className={`mw-btn-send ${!input.trim()&&!previewArq&&!previewAudio?'disabled':''}`}
                onClick={handleEnviar}
                disabled={(!input.trim()&&!previewArq&&!previewAudio)||enviandoMsg||uploadingArq||gravando}>
                {enviandoMsg||uploadingArq?<FaSync className="mw-spin"/>:'Enviar ↵'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}